import React from "react";
import {
  Body1,
  Button,
  Caption1,
  Card,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  MessageBar,
  MessageBarBody,
  Spinner,
  Textarea,
  Title3,
  Tooltip,
  makeStyles,
  shorthands,
  tokens,
} from "@fluentui/react-components";
import { AddRegular, DismissRegular, EditRegular, SearchRegular } from "@fluentui/react-icons";
import { useMsal } from "@azure/msal-react";
import { Page } from "../components/App";
import { useJob } from "../context/JobContext";
import type { BenSpecQuestionRow, BenSpecQuestionSetMeta } from "../types";
import { applyGuidanceEdit } from "../services/benSpecGuidance";
import {
  benSpecQuestionsToCsv,
  buildHeadingPath,
  parseBenSpecQuestionsCsv,
} from "../services/benSpecQuestionsCsv";
import { getAccessToken } from "../services/authService";
import {
  fetchBenSpecQuestionLibrary,
  normaliseBenSpecQuestionLibrary,
  UiBenSpecQuestionSet,
} from "../services/benSpecQuestionProfileService";
import { taskPaneBackground } from "../theme";

const useStyles = makeStyles({
  root: {
    height: "88vh",
    display: "flex",
    flexDirection: "column",
    ...taskPaneBackground,
    ...shorthands.gap(tokens.spacingVerticalS),
    ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalL),
    boxSizing: "border-box",
    overflow: "hidden",
    minHeight: 0,
  },
  headerCard: {
    flexShrink: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", "#dbe4ef"),
    ...shorthands.borderRadius("14px"),
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalL),
    boxShadow: tokens.shadow4,
  },
  headerEyebrow: {
    color: "#0f6cbd",
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    marginBottom: "1px",
  },
  headerTitle: {
    margin: 0,
  },
  headerSubtitle: {
    margin: 0,
    color: tokens.colorNeutralForeground3,
  },
  toolbar: {
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap(tokens.spacingVerticalXS),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", "#dbe4ef"),
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
    ...shorthands.padding(tokens.spacingVerticalS),
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
  },
  setRow: {
    display: "flex",
    alignItems: "flex-end",
    ...shorthands.gap(tokens.spacingHorizontalS),
    flexWrap: "wrap",
  },
  setField: {
    flex: "0 1 420px",
    width: "100%",
    maxWidth: "420px",
    minWidth: 0,
  },
  setSelect: {
    width: "100%",
    minHeight: "32px",
    color: tokens.colorNeutralForeground1,
    backgroundColor: "#f8fafc",
    ...shorthands.border("1px", "solid", "#cbd5e1"),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.padding(0, tokens.spacingHorizontalS),
  },
  toolbarRow: {
    display: "flex",
    flexWrap: "nowrap",
    alignItems: "center",
    ...shorthands.gap(tokens.spacingHorizontalS),
  },
  searchInput: {
    flex: "1 1 auto",
    minWidth: 0,
  },
  menuButton: {
    flexShrink: 0,
    minHeight: "32px",
  },
  listShell: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", "#dbe4ef"),
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
    ...shorthands.padding(tokens.spacingVerticalM),
    boxShadow: tokens.shadow4,
  },
  listHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
    ...shorthands.gap(tokens.spacingHorizontalS),
    paddingBottom: tokens.spacingVerticalS,
  },
  listHeading: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  countBadge: {
    flexShrink: 0,
    color: "#0f6cbd",
    backgroundColor: "#eff6ff",
    ...shorthands.border("1px", "solid", "#bfdbfe"),
    ...shorthands.borderRadius("999px"),
    ...shorthands.padding(tokens.spacingVerticalXXS, tokens.spacingHorizontalS),
    fontWeight: tokens.fontWeightSemibold,
  },
  listScroll: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    ...shorthands.padding(tokens.spacingVerticalXXS, tokens.spacingHorizontalXXS),
  },
  listEmpty: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    ...shorthands.padding(tokens.spacingVerticalL),
  },
  row: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    ...shorthands.gap(tokens.spacingHorizontalS),
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
    marginBottom: tokens.spacingVerticalS,
    backgroundColor: "#f8fafc",
    ...shorthands.border("1px", "solid", "#dbe4ef"),
    ...shorthands.borderRadius("10px"),
  },
  rowMain: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap(tokens.spacingVerticalXXS),
  },
  rowActions: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
    ...shorthands.gap(tokens.spacingHorizontalXXS),
  },
  questionPath: {
    color: "#0f6cbd",
    fontWeight: tokens.fontWeightSemibold,
  },
  footerCard: {
    flexShrink: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", "#dbe4ef"),
    ...shorthands.borderRadius(tokens.borderRadiusXLarge),
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalL),
    boxShadow: tokens.shadow4,
  },
  footerActions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    ...shorthands.gap(tokens.spacingHorizontalS),
  },
});

interface BenSpecQuestionsPageProps {
  onNavigate: (page: Page) => void;
}

function newRowId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return `ben-spec-new-${crypto.randomUUID()}`;
  }
  return `ben-spec-new-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function normalizeQuestionRow(row: BenSpecQuestionRow): BenSpecQuestionRow {
  const section = row.section.trim() || "custom";
  const subSection = (row.subSection || "").trim();
  const subSubSection = (row.subSubSection || "").trim();
  // Always rebuild from the current hierarchy fields; a cached headingPath
  // goes stale once the user edits section/sub-section/sub-sub-section.
  const headingPath = buildHeadingPath(section, subSection, subSubSection);
  return {
    ...row,
    section,
    subSection,
    subSubSection,
    headingPath,
    questionLabel: row.questionLabel.trim(),
    question: row.question.trim(),
    guidance: row.guidance || null,
  };
}

async function exportCsv(csv: string): Promise<"downloaded" | "copied"> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(csv);
    return "copied";
  }
  throw new Error("Clipboard API unavailable");
}

function matchesFilter(row: BenSpecQuestionRow, q: string): boolean {
  if (!q.trim()) {
    return true;
  }
  const s = q.trim().toLowerCase();
  return (
    row.section.toLowerCase().includes(s) ||
    row.questionLabel.toLowerCase().includes(s) ||
    row.question.toLowerCase().includes(s)
  );
}

function metaFromQuestionSet(questionSet: UiBenSpecQuestionSet): BenSpecQuestionSetMeta {
  return {
    id: questionSet.id,
    name: questionSet.name,
    source: questionSet.source,
    version: questionSet.version,
    snapshotHash: questionSet.snapshotHash,
  };
}

function labelForQuestionSet(questionSet: UiBenSpecQuestionSet): string {
  const sourceLabel =
    questionSet.source === "recent"
      ? "Recent"
      : questionSet.source === "published"
        ? "Published"
        : "Default";
  const version = questionSet.version ? ` v${questionSet.version}` : "";
  return `${sourceLabel}: ${questionSet.name}${version}`;
}

export const BenSpecQuestionsPage: React.FC<BenSpecQuestionsPageProps> = ({ onNavigate }) => {
  const styles = useStyles();
  const { state, dispatch } = useJob();
  const { instance, accounts } = useMsal();
  const [search, setSearch] = React.useState("");
  const [editRow, setEditRow] = React.useState<BenSpecQuestionRow | null>(null);
  const [editError, setEditError] = React.useState<string | null>(null);
  const [csvError, setCsvError] = React.useState<string | null>(null);
  const [actionNotice, setActionNotice] = React.useState<string | null>(null);
  const [libraryError, setLibraryError] = React.useState<string | null>(null);
  const [exportPreview, setExportPreview] = React.useState<{ title: string; csv: string } | null>(
    null
  );
  const [pendingImport, setPendingImport] = React.useState<BenSpecQuestionRow[] | null>(null);
  const [resetOpen, setResetOpen] = React.useState(false);
  const [questionSets, setQuestionSets] = React.useState<UiBenSpecQuestionSet[]>([]);
  const [loadingLibrary, setLoadingLibrary] = React.useState<boolean>(
    state.benSpecQuestions.length === 0
  );
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // The dropdown options (`questionSets`) are component-local, so they
  // are wiped whenever the user navigates away and back. The working
  // catalogue (`state.benSpecQuestions`) survives in JobContext. The
  // effect must therefore fetch the library on every fresh mount to
  // repopulate the dropdown, while only *seeding* the working list when
  // it is genuinely empty — re-fetching must never overwrite the user's
  // in-progress edits.
  const didLoadLibraryRef = React.useRef(false);
  const loadLibraryRef = React.useRef<() => Promise<void>>(async () => undefined);
  React.useEffect(() => {
    if (didLoadLibraryRef.current) {
      return;
    }
    if (accounts.length === 0) {
      return;
    }
    didLoadLibraryRef.current = true;
    const loadQuestionLibrary = async () => {
      const hasExistingRows = state.benSpecQuestions.length > 0;
      setLibraryError(null);
      // Inline spinner is only meaningful when we have nothing to
      // show yet. On re-mount with a populated list we refresh the
      // dropdown options silently in the background.
      if (!hasExistingRows) {
        setLoadingLibrary(true);
      }
      try {
        const token = await getAccessToken(instance);
        const library = normaliseBenSpecQuestionLibrary(await fetchBenSpecQuestionLibrary(token));
        setQuestionSets(library.options);
        // Cache the default set so "Reset to defaults" can restore it
        // later without another round trip, and so the reset-copy can
        // surface the right question count.
        dispatch({
          type: "SET_BEN_SPEC_DEFAULT_SET",
          payload: {
            rows: library.defaultSet.rows.map(normalizeQuestionRow),
            meta: metaFromQuestionSet(library.defaultSet),
          },
        });
        if (!hasExistingRows) {
          applyQuestionSet(library.defaultSet);
        }
      } catch {
        // Surface a real, actionable error rather than silently seeding
        // local rows. The add-in cannot submit jobs without backend
        // reachability anyway, so a transient library failure is best
        // shown as something the user can retry.
        setLibraryError("Couldn't load the question library. Check your connection and try again.");
        // Let the user retry; we already decided not to load.
        didLoadLibraryRef.current = false;
      } finally {
        setLoadingLibrary(false);
      }
    };
    loadLibraryRef.current = loadQuestionLibrary;
    loadQuestionLibrary().catch(() => undefined);
  }, [accounts.length, dispatch, instance]);

  const retryLoadLibrary = () => {
    loadLibraryRef.current().catch(() => undefined);
  };

  const rows = state.benSpecQuestions;
  const filtered = React.useMemo(
    () => rows.filter((r) => matchesFilter(r, search)),
    [rows, search]
  );
  const shownCount = filtered.length;
  const isAddingCustomQuestion = editRow !== null && !rows.some((row) => row.id === editRow.id);

  const replaceAll = (next: BenSpecQuestionRow[]) => {
    dispatch({ type: "SET_BEN_SPEC_QUESTIONS", payload: next });
  };

  const applyQuestionSet = (questionSet: UiBenSpecQuestionSet) => {
    replaceAll(questionSet.rows.map(normalizeQuestionRow));
    dispatch({ type: "SET_BEN_SPEC_QUESTION_SET", payload: metaFromQuestionSet(questionSet) });
  };

  const handleQuestionSetChange = (id: string) => {
    const next = questionSets.find((item) => item.id === id);
    if (next) {
      applyQuestionSet(next);
    }
  };

  const handleAddRow = () => {
    setEditError(null);
    setEditRow({
      id: newRowId(),
      section: "",
      subSection: "",
      subSubSection: "",
      questionLabel: "",
      question: "",
      headingPath: [],
      guidance: null,
    });
  };

  const handleRemove = (id: string) => {
    replaceAll(rows.filter((r) => r.id !== id));
  };

  const handleSaveEdit = () => {
    if (!editRow) {
      return;
    }
    const normalizedRow = normalizeQuestionRow(editRow);
    if (!normalizedRow.questionLabel) {
      setEditError("Add a question label before saving.");
      return;
    }
    const previousRow = rows.find((r) => r.id === editRow.id);
    if (previousRow) {
      const rowToSave = applyGuidanceEdit(previousRow, normalizedRow);
      replaceAll(rows.map((r) => (r.id === rowToSave.id ? rowToSave : r)));
    } else {
      replaceAll([...rows, { ...normalizedRow, guidanceRemoved: false }]);
    }
    setActionNotice("Question saved.");
    setEditError(null);
    setEditRow(null);
  };

  const handleExportCurrent = async () => {
    const csv = benSpecQuestionsToCsv(rows);
    try {
      await exportCsv(csv);
      setActionNotice("Current list copied to clipboard.");
    } catch {
      setExportPreview({
        title: "Current questions CSV",
        csv,
      });
      setActionNotice("Clipboard copy was blocked. Use the popup to copy manually.");
    }
  };

  const onCsvFile = (file: File) => {
    setCsvError(null);
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || "");
      const parsed = parseBenSpecQuestionsCsv(text);
      if ("error" in parsed) {
        setCsvError(parsed.error);
        return;
      }
      const invalidRow = parsed.rows.find((row) => !row.questionLabel.trim());
      if (invalidRow) {
        setCsvError("Each imported row must include a Question Label.");
        return;
      }
      setPendingImport(parsed.rows.map(normalizeQuestionRow));
    };
    reader.onerror = () => {
      setCsvError("Could not read the file.");
    };
    reader.readAsText(file);
  };

  const applyReset = () => {
    const cachedDefault = state.benSpecDefaultSet;
    if (!cachedDefault) {
      // Defensive: the Reset button is only enabled when the default is
      // cached, but guard here so a stale dialog open after a failed
      // load doesn't replace the list with nothing.
      setResetOpen(false);
      return;
    }
    // Deep-clone heading paths so subsequent edits don't mutate the cached
    // default set held in context.
    const restoredRows = cachedDefault.rows.map((row) => ({
      ...row,
      headingPath: [...(row.headingPath || [])],
    }));
    replaceAll(restoredRows);
    dispatch({ type: "SET_BEN_SPEC_QUESTION_SET", payload: cachedDefault.meta });
    setActionNotice("Default questions restored.");
    setResetOpen(false);
  };

  const cachedDefaultRowCount = state.benSpecDefaultSet?.rows.length ?? 0;
  const canResetToDefault = cachedDefaultRowCount > 0;
  const canContinue = rows.length > 0 && rows.every((row) => row.questionLabel.trim().length > 0);

  return (
    <div className={styles.root}>
      <Card className={styles.headerCard}>
        <div className={styles.headerEyebrow}>Benefit specification · Step 2</div>
        <Title3 className={styles.headerTitle}>Build question set</Title3>
        <Body1 className={styles.headerSubtitle}>
          Review and refine the questions that will shape the final specification.
        </Body1>
      </Card>

      <div className={styles.toolbar}>
        <div className={styles.setRow}>
          {questionSets.length > 0 && (
            <Field className={styles.setField} label="Question set">
              <select
                className={styles.setSelect}
                value={state.benSpecQuestionSet.id || ""}
                onChange={(event) => handleQuestionSetChange(event.target.value)}
              >
                {questionSets.map((item) => (
                  <option key={item.id} value={item.id}>
                    {labelForQuestionSet(item)}
                  </option>
                ))}
              </select>
            </Field>
          )}
        </div>
        <div className={styles.toolbarRow}>
          <Input
            className={styles.searchInput}
            placeholder="Search sections, labels, or questions…"
            value={search}
            onChange={(_, d) => setSearch(d.value)}
            contentBefore={<SearchRegular aria-hidden style={{ marginLeft: 8 }} />}
          />
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button className={styles.menuButton}>More actions</Button>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem icon={<AddRegular />} onClick={handleAddRow}>
                  Add question
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    fileInputRef.current?.click();
                  }}
                >
                  Import CSV (replace list)…
                </MenuItem>
                <MenuItem onClick={handleExportCurrent}>Copy current list to clipboard</MenuItem>
                <MenuItem
                  disabled={!canResetToDefault}
                  onClick={() => {
                    if (canResetToDefault) {
                      setResetOpen(true);
                    }
                  }}
                >
                  Reset to defaults…
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (f) {
                onCsvFile(f);
              }
            }}
          />
        </div>
        {libraryError && (
          <MessageBar intent="error">
            <MessageBarBody>
              {libraryError}{" "}
              <Button
                appearance="transparent"
                size="small"
                onClick={retryLoadLibrary}
                disabled={loadingLibrary}
              >
                Retry
              </Button>
            </MessageBarBody>
          </MessageBar>
        )}
        {csvError && (
          <MessageBar intent="error">
            <MessageBarBody>{csvError}</MessageBarBody>
          </MessageBar>
        )}
        {actionNotice && (
          <MessageBar intent="success">
            <MessageBarBody>{actionNotice}</MessageBarBody>
          </MessageBar>
        )}
      </div>

      <div className={styles.listShell}>
        <div className={styles.listHeader}>
          <div className={styles.listHeading}>
            <Body1 style={{ fontWeight: tokens.fontWeightSemibold }}>Questions</Body1>
            <Caption1>
              {search.trim() ? `${shownCount} matching questions` : "Edit or remove any item"}
            </Caption1>
          </div>
          <span className={styles.countBadge}>
            {rows.length} question{rows.length === 1 ? "" : "s"}
          </span>
        </div>
        <div className={styles.listScroll}>
          {loadingLibrary ? (
            <div className={styles.listEmpty}>
              <Spinner size="small" label="Loading questions…" />
            </div>
          ) : filtered.length === 0 ? (
            <div className={styles.listEmpty}>
              <Body1>
                {rows.length === 0
                  ? "No questions yet. Use Add or Import CSV to get started."
                  : "No rows match your search."}
              </Body1>
            </div>
          ) : (
            filtered.map((row) => (
              <div key={row.id} className={styles.row}>
                <div className={styles.rowMain}>
                  <Caption1 className={styles.questionPath}>
                    {[row.section || "Custom", row.subSection, row.subSubSection]
                      .filter(Boolean)
                      .join(" · ")}
                  </Caption1>
                  <Body1
                    title={row.questionLabel}
                    style={{ fontWeight: tokens.fontWeightSemibold }}
                  >
                    {row.questionLabel || "(No question label)"}
                  </Body1>
                </div>
                <div className={styles.rowActions}>
                  <Tooltip content="Edit question" relationship="label" withArrow>
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<EditRegular />}
                      aria-label="Edit question"
                      onClick={() => setEditRow({ ...row })}
                    />
                  </Tooltip>
                  <Tooltip content="Remove question" relationship="label" withArrow>
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<DismissRegular style={{ color: tokens.colorPaletteRedForeground1 }} />}
                      aria-label="Remove question"
                      onClick={() => handleRemove(row.id)}
                    />
                  </Tooltip>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <Card className={styles.footerCard}>
        <div className={styles.footerActions}>
          <Button
            appearance="primary"
            className="sourcestone-navigation-primary"
            onClick={() => onNavigate("benSpecFileSelection")}
          >
            Back
          </Button>
          <Button
            appearance="primary"
            className="sourcestone-navigation-primary"
            disabled={!canContinue}
            onClick={() => onNavigate("benSpecConfirmation")}
          >
            Continue
          </Button>
        </div>
      </Card>

      <Dialog open={!!editRow} onOpenChange={(_, d) => !d.open && setEditRow(null)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              {isAddingCustomQuestion ? "Add custom question" : "Edit question"}
            </DialogTitle>
            <DialogContent>
              {editRow && (
                <>
                  {editError && (
                    <MessageBar intent="error">
                      <MessageBarBody>{editError}</MessageBarBody>
                    </MessageBar>
                  )}
                  <Field label="Section">
                    <Input
                      value={editRow.section}
                      onChange={(_, v) => setEditRow({ ...editRow, section: v.value })}
                    />
                  </Field>
                  <Field label="Sub Section">
                    <Input
                      value={editRow.subSection || ""}
                      onChange={(_, v) => setEditRow({ ...editRow, subSection: v.value })}
                    />
                  </Field>
                  <Field label="Sub Sub section">
                    <Input
                      value={editRow.subSubSection || ""}
                      onChange={(_, v) => setEditRow({ ...editRow, subSubSection: v.value })}
                    />
                  </Field>
                  <Field label="Question label (value for report)">
                    <Input
                      value={editRow.questionLabel}
                      required
                      onChange={(_, v) => setEditRow({ ...editRow, questionLabel: v.value })}
                    />
                  </Field>
                  <Field label="Question" hint="Full question text.">
                    <Textarea
                      resize="vertical"
                      style={{ minHeight: 100 }}
                      value={editRow.question}
                      onChange={(_, v) => setEditRow({ ...editRow, question: v.value })}
                    />
                  </Field>
                  <Field
                    label="Guidance"
                    hint={
                      isAddingCustomQuestion ? "do not enter PII data into this field" : undefined
                    }
                  >
                    <Textarea
                      resize="vertical"
                      style={{ minHeight: 70 }}
                      value={editRow.guidance || ""}
                      onChange={(_, v) => setEditRow({ ...editRow, guidance: v.value })}
                    />
                  </Field>
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => {
                  setEditError(null);
                  setEditRow(null);
                }}
              >
                Cancel
              </Button>
              <Button appearance="primary" onClick={handleSaveEdit}>
                Save
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <Dialog open={!!pendingImport} onOpenChange={(_, d) => !d.open && setPendingImport(null)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Replace all questions?</DialogTitle>
            <DialogContent>
              This will replace your current {rows.length} row{rows.length === 1 ? "" : "s"} with{" "}
              {pendingImport?.length ?? 0} row{pendingImport?.length === 1 ? "" : "s"} from the CSV.
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setPendingImport(null)}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={() => {
                  if (pendingImport) {
                    replaceAll(pendingImport);
                    dispatch({
                      type: "SET_BEN_SPEC_QUESTION_SET",
                      payload: {
                        id: null,
                        name: "Imported CSV",
                        source: "imported_csv",
                        version: null,
                        snapshotHash: null,
                      },
                    });
                  }
                  setPendingImport(null);
                }}
              >
                Replace list
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <Dialog open={resetOpen} onOpenChange={(_, d) => !d.open && setResetOpen(false)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Reset to defaults?</DialogTitle>
            <DialogContent>
              Your edits will be discarded and the standard {cachedDefaultRowCount} question
              {cachedDefaultRowCount === 1 ? "" : "s"} will be restored.
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setResetOpen(false)}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={applyReset}>
                Reset
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <Dialog open={!!exportPreview} onOpenChange={(_, d) => !d.open && setExportPreview(null)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{exportPreview?.title ?? "CSV export"}</DialogTitle>
            <DialogContent>
              <Body1>
                The CSV is shown below so you can copy it directly if the Excel webview suppresses
                downloads.
              </Body1>
              <Textarea
                resize="vertical"
                style={{ minHeight: 220, marginTop: 12 }}
                value={exportPreview?.csv ?? ""}
                readOnly
              />
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setExportPreview(null)}>
                Close
              </Button>
              <Button
                appearance="primary"
                onClick={async () => {
                  if (!exportPreview?.csv) {
                    return;
                  }
                  try {
                    await navigator.clipboard.writeText(exportPreview.csv);
                    setActionNotice("CSV copied to clipboard.");
                  } catch {
                    setCsvError(
                      "Clipboard copy was blocked. Select the CSV text and copy it manually."
                    );
                  }
                }}
              >
                Copy CSV
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};
